import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useHistory } from 'react-router-dom';
import api from '../../../domain/api';

const fetchAccounts = async () => {
  const res = await api.get('/facebook-ads/ad-accounts');
  return res.data.adAccounts || [];
};

const FacebookAdsAccountsReport = () => {
  const history = useHistory();
  const [filters, setFilters] = useState({
    accountName: '',
    clientName: '',
    status: '',
    businessName: '',
  });

  const {
    data: accounts = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['facebook-ads-accounts'],
    queryFn: fetchAccounts,
  });

  // Filter accounts based on current filters
  const filteredAccounts = useMemo(() => {
    return accounts.filter((acc) => {
      const matchesAccountName =
        !filters.accountName ||
        acc.accountName
          ?.toLowerCase()
          .includes(filters.accountName.toLowerCase());

      const matchesClientName =
        !filters.clientName ||
        acc.clientName
          ?.toLowerCase()
          .includes(filters.clientName.toLowerCase());

      const matchesStatus =
        !filters.status ||
        acc.status?.toLowerCase() === filters.status.toLowerCase();

      const matchesBusinessName =
        !filters.businessName ||
        acc.businessName
          ?.toLowerCase()
          .includes(filters.businessName.toLowerCase());

      return (
        matchesAccountName &&
        matchesClientName &&
        matchesStatus &&
        matchesBusinessName
      );
    });
  }, [accounts, filters]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const clearFilters = () => {
    setFilters({
      accountName: '',
      clientName: '',
      status: '',
      businessName: '',
    });
  };

  const getUniqueStatuses = () => {
    const statuses = accounts.map((acc) => acc.status).filter(Boolean);
    return [...new Set(statuses)];
  };

  return (
    <div className='meta-ads-container'>
      <div className='meta-ads-header'>
        <div className='header-left'>
          <h1 className='header-title'>
            <i
              className='fa fa-facebook-square'
              style={{ marginRight: '8px' }}
            ></i>
            Facebook Ads Accounts Report
          </h1>
        </div>
      </div>

      {/* Filters Section */}
      <div className='filters-container'>
        <div className='filters-row'>
          <div className='filter-group'>
            <label>Account Name</label>
            <input
              type='text'
              placeholder='Filter by account name...'
              value={filters.accountName}
              onChange={(e) =>
                handleFilterChange('accountName', e.target.value)
              }
              className='filter-input'
            />
          </div>

          <div className='filter-group'>
            <label>Client Name</label>
            <input
              type='text'
              placeholder='Filter by client name...'
              value={filters.clientName}
              onChange={(e) => handleFilterChange('clientName', e.target.value)}
              className='filter-input'
            />
          </div>

          <div className='filter-group'>
            <label>Business Name</label>
            <input
              type='text'
              placeholder='Filter by business name...'
              value={filters.businessName}
              onChange={(e) =>
                handleFilterChange('businessName', e.target.value)
              }
              className='filter-input'
            />
          </div>

          <div className='filter-group'>
            <label>Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className='filter-select'
            >
              <option value=''>All Statuses</option>
              {getUniqueStatuses().map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </div>

          <div className='filter-actions'>
            <button
              onClick={clearFilters}
              className='clear-filters-btn'
              title='Clear all filters'
            >
              <i className='fa fa-times'></i> Clear
            </button>
          </div>
        </div>

        <div className='filter-results'>
          <span className='results-count'>
            Showing {filteredAccounts.length} of {accounts.length} accounts
          </span>
        </div>
      </div>
      <div className='meta-table-container'>
        {isLoading ? (
          <div className='loading-container'>
            <div className='loading-spinner'></div>
            <p>Loading accounts...</p>
          </div>
        ) : isError ? (
          <div className='loading-container'>
            <i
              className='fa fa-exclamation-triangle'
              style={{ fontSize: 24, color: '#c0392b', marginBottom: 12 }}
            ></i>
            <p>{error?.message || 'Failed to fetch accounts'}</p>
          </div>
        ) : filteredAccounts.length === 0 ? (
          <div className='loading-container'>
            <i
              className='fa fa-search'
              style={{ fontSize: 24, marginBottom: 12, opacity: 0.5 }}
            ></i>
            <p>
              {accounts.length > 0
                ? 'No accounts match your filters'
                : 'No accounts found'}
            </p>
          </div>
        ) : (
          <table className='meta-table'>
            <thead className='meta-table-header'>
              <tr>
                <th>Account Name</th>
                <th>Account ID</th>
                <th>Business Name</th>
                <th>Client Name</th>
                <th>Status</th>
                <th>Payment Type</th>
                <th>Available Balance</th>
                <th>Amount Spent</th>
                <th>Spend Cap</th>
                <th>Currency</th>
              </tr>
            </thead>
            <tbody className='meta-table-body'>
              {filteredAccounts.map((acc) => (
                <tr key={acc.accountId}>
                  <td>
                    <button
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        margin: 0,
                        color: '#1877f2',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        fontWeight: 600,
                      }}
                      title='View campaigns'
                      onClick={() => {
                        const params = new URLSearchParams();
                        params.set('tab', 'campaigns');
                        params.set('accountId', acc.accountId);
                        history.push({
                          pathname: '/facebook-ads-reports',
                          search: params.toString(),
                        });
                      }}
                    >
                      {acc.accountName}
                    </button>
                  </td>
                  <td>
                    <span className='account-id'>{acc.accountId}</span>
                  </td>
                  <td>{acc.businessName || 'N/A'}</td>
                  <td>
                    {acc.clientName ? (
                      <button
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          margin: 0,
                          color: '#7c3aed',
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          fontWeight: 600,
                        }}
                        title='View campaigns'
                        onClick={() => {
                          const params = new URLSearchParams();
                          params.set('tab', 'campaigns');
                          params.set('accountId', acc.accountId);
                          history.push({
                            pathname: '/facebook-ads-reports',
                            search: params.toString(),
                          });
                        }}
                      >
                        {acc.clientName}
                      </button>
                    ) : (
                      'N/A'
                    )}
                  </td>
                  <td>
                    <span
                      className={`account-status ${acc.status?.toLowerCase()}`}
                      title={acc.status}
                    >
                      <i
                        className='fa fa-circle'
                        style={{
                          color:
                            acc.status?.toLowerCase() === 'active'
                              ? '#1a7f37'
                              : '#c0392b',
                        }}
                      ></i>
                      {acc.status}
                    </span>
                  </td>
                  <td>
                    <span className='payment-type'>{acc.paymentType}</span>
                  </td>
                  <td>
                    <span className='balance-value'>
                      {acc.availableBalance}
                    </span>
                  </td>
                  <td>
                    <span className='spent-value'>{acc.amountSpent}</span>
                  </td>
                  <td>
                    <span className='spend-cap'>{acc.spendCap}</span>
                  </td>
                  <td>{acc.currency}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <style jsx>{`
        .meta-ads-container {
          background: #f5f6f7;
          min-height: 100vh;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
            Helvetica, Arial, sans-serif;
        }
        .meta-ads-header {
          background: linear-gradient(135deg, #1877f2 0%, #166fe5 100%);
          color: white;
          padding: 12px 20px;
          display: flex;
          align-items: center;
          justify-content: flex-start;
        }
        .header-title {
          font-size: 18px;
          font-weight: 600;
          margin: 0;
        }
        .filters-container {
          background: white;
          border: 1px solid #e4e6ea;
          border-radius: 10px;
          margin: 16px 0;
          padding: 16px;
          box-shadow: 0 1px 6px rgba(0, 0, 0, 0.04);
        }
        .filters-row {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 16px;
          align-items: end;
        }
        .filter-group {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .filter-group label {
          font-size: 12px;
          font-weight: 600;
          color: #65676b;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .filter-input,
        .filter-select {
          padding: 8px 12px;
          border: 1px solid #ddd;
          border-radius: 6px;
          font-size: 13px;
          background: white;
          transition: all 0.2s ease;
        }
        .filter-input:focus,
        .filter-select:focus {
          outline: none;
          border-color: #1877f2;
          box-shadow: 0 0 0 2px rgba(24, 119, 242, 0.1);
        }
        .filter-input::placeholder {
          color: #999;
          font-style: italic;
        }
        .filter-actions {
          display: flex;
          align-items: end;
          gap: 8px;
        }
        .clear-filters-btn {
          padding: 8px 16px;
          background: #f8f9fa;
          border: 1px solid #ddd;
          border-radius: 6px;
          color: #65676b;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .clear-filters-btn:hover {
          background: #e9ecef;
          border-color: #bbb;
        }
        .filter-results {
          margin-top: 12px;
          padding-top: 12px;
          border-top: 1px solid #f0f2f5;
        }
        .results-count {
          font-size: 12px;
          color: #65676b;
          font-weight: 500;
        }
        .meta-table-container {
          position: relative;
          overflow-x: auto;
          max-height: calc(100vh - 200px);
          border: 1px solid #e4e6ea;
          background: #fff;
          border-radius: 10px;
          box-shadow: 0 1px 6px rgba(0, 0, 0, 0.04);
          margin: 24px 0;
        }
        .meta-table {
          width: 100%;
          min-width: 1200px;
          border-collapse: collapse;
          background: white;
          font-size: 13px;
        }
        .meta-table-header {
          background: #f8f9fa;
          border-bottom: 2px solid #e4e6ea;
          position: sticky;
          top: 0;
          z-index: 10;
        }
        .meta-table-header th {
          padding: 8px 12px;
          text-align: left;
          font-size: 11px;
          font-weight: 600;
          color: #65676b;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          white-space: nowrap;
          border-right: 1px solid #e4e6ea;
        }
        .meta-table-header th:last-child {
          border-right: none;
        }
        .meta-table-header th:hover {
          background: #e9ecef;
        }
        .meta-table-body tr {
          border-bottom: 1px solid #f0f2f5;
        }
        .meta-table-body tr:hover {
          background: #f8f9fa;
        }
        .meta-table-body td {
          padding: 8px 12px;
          font-size: 13px;
          color: #1c1e21;
          vertical-align: top;
          border-right: 1px solid #f0f2f5;
        }
        .meta-table-body td:last-child {
          border-right: none;
        }
        .account-id {
          font-family: 'Courier New', monospace;
          background: #f5f5f5;
          padding: 1px 5px;
          border-radius: 3px;
          font-weight: 500;
        }
        .account-status {
          display: flex;
          align-items: center;
          gap: 5px;
          padding: 2px 8px 2px 6px;
          border-radius: 8px;
          font-size: 11px;
          font-weight: 600;
          text-transform: capitalize;
          letter-spacing: 0.2px;
          background: #f5f5f5;
          color: #555;
          min-width: 70px;
          border: 1px solid #e4e6ea;
        }
        .account-status.active {
          background: #e6f9ed;
          color: #1a7f37;
          border: 1px solid #b6e7c9;
        }
        .account-status.disabled,
        .account-status.inactive {
          background: #fbeaea;
          color: #c0392b;
          border: 1px solid #f5c6cb;
        }
        .account-status i {
          font-size: 9px;
          margin-right: 2px;
        }
        .payment-type {
          background: #e3f2fd;
          color: #1565c0;
          padding: 2px 8px;
          border-radius: 6px;
          font-size: 11px;
          font-weight: 600;
          text-transform: capitalize;
          border: 1px solid #bbdefb;
        }
        .balance-value {
          font-weight: 600;
          color: #2e7d32;
        }
        .spent-value {
          font-weight: 600;
          color: #d84315;
        }
        .spend-cap {
          font-weight: 500;
          color: #f57c00;
        }
        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
          color: #65676b;
        }
        .loading-spinner {
          width: 24px;
          height: 24px;
          border: 2px solid #f0f2f5;
          border-top: 2px solid #1877f2;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 12px;
        }
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
};

export default FacebookAdsAccountsReport;
